import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Components
import { Button, IconButton } from 'components/Buttons';
import { Checkbox, Select } from 'components/Controls';
import { Tooltip } from 'components/Tooltip';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import ChoicesEditor from './ChoicesEditor';
// Types
import RegistrationFieldTypes from 'types/RegistrationFieldTypes';
import RegistrationFieldBindings from 'types/RegistrationFieldBindings';
// Icons
import { FaTrash } from "react-icons/fa";
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

type Props = {
  fields: any[];
  append: (value:any) => void;
  remove: (index:number) => void;
}

const CustomFields:FC<Props> = ({ fields, append, remove }) => {
  const { t } = useTranslation();

  const { control, watch, formState: { errors } } = useFormContext();

  const handleAdd = () => {
    append({ ref: uuidv4(), title: '', type: RegistrationFieldTypes.ShortText, required: false });
  }

  const handleRemove = (index:number) => () => {
    remove(index);
  }

  // The team selector (binding: 'team') is managed by its own dedicated UI
  // (TeamSelectorField), even though it lives in this same fields[] array.
  const visibleFields = fields
    .map((field:any, index:number) => ({ field, index }))
    .filter(({ field }) => field.binding !== RegistrationFieldBindings.Team);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h4 className="text-xl sm:text-2xl font-semibold mb-4 text-white">{t('pages.quizForm.customFieldsLabel')}</h4>
        <Button variant="contained" onClick={handleAdd}>
          {t('buttons.addField')}
        </Button>
      </div>
      <span className="block text-gray-400 text-sm font-light leading-5 my-2">{t('pages.quizForm.customFieldsHint')}</span>
      {visibleFields.map(({ field, index }, displayIndex) => {
        const watchedType = watch(`registrationPage.fields.${index}.type`);

        return (
          <div key={`field-item-${field.id}`} className="mb-4 pb-4 border-b border-white/10">
            <div className="flex gap-2 items-start flex-wrap">
              <div className="flex-grow min-w-[200px]">
                <MultiLanguageTextField
                  name={`registrationPage.fields.${index}.title`}
                  rules={{ required: isRequired, validate: noWhitespace }}
                  label={`${t('labels.field')} ${displayIndex + 1}`}
                  className="mb-4"
                  required
                  error={Boolean(errors.registrationPage?.fields?.[index]?.title)}
                  errorText={errors.registrationPage?.fields?.[index]?.title?.message || ''}
                  customKey={`registrationPage.fields.${field.ref}.title`}
                />
              </div>
              <div className="w-64">
                <Controller
                  control={control} name={`registrationPage.fields.${index}.type`}
                  render={({ field: typeField }) => (
                    <Select
                      id={`registrationPage.fields.${index}.type`}
                      {...typeField}
                      label={t('labels.fieldType')}
                      className="mb-4"
                      options={[
                        { value: RegistrationFieldTypes.ShortText, label: t('labels.fieldTypeShortText') },
                        { value: RegistrationFieldTypes.SingleChoice, label: t('labels.fieldTypeSingleChoice') },
                      ]}
                    />
                  )}
                />
              </div>
              <div className="mt-6">
                <Tooltip title={t('buttons.remove')}>
                  <IconButton
                    onClick={handleRemove(index)}
                  ><FaTrash /></IconButton>
                </Tooltip>
              </div>
            </div>
            <Controller
              control={control} name={`registrationPage.fields.${index}.required`}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  className="mb-4"
                  id={`registrationPage.fields.${index}.required`}
                  name={`registrationPage.fields.${index}.required`}
                  label={t('labels.required')}
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
            {watchedType === RegistrationFieldTypes.SingleChoice ? (
              <ChoicesEditor name={`registrationPage.fields.${index}.choices`} fieldRef={field.ref} />
            ) : null}
          </div>
        );
      })}
    </div>
  )
}

export default CustomFields;
